import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Search, RefreshCw, TrendingUp, TrendingDown, Play, Image as ImageIcon,
    AlertTriangle, CheckCircle, ChevronDown, ChevronUp, Calendar,
    BarChart3, Sparkles, Eye, MousePointer, DollarSign, Target,
    Users, Repeat, ArrowRight, X, GitCompare, ZoomIn, Download,
    Clock, Pause, Archive, Trash2
} from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import AnalysisChat from '../components/AnalysisChat';
import {
    formatNumber, formatCurrency, formatPercent,
    DATE_RANGE_PRESETS, DATE_RANGE_LABELS, getDateRange
} from '../utils/helpers';

// Cache configuration
const CACHE_EXPIRY_HOURS = 24;
const ANALYSIS_CACHE_KEY_PREFIX = 'creative-analysis-';
const CREATIVES_CACHE_KEY_PREFIX = 'creatives-list-';

// Creative Detail Modal Component
const CreativeDetailModal = ({ creative, onClose, getCacheBustedUrl }) => {
    if (!creative) return null;

    // Fallback if getCacheBustedUrl not provided
    const getUrl = getCacheBustedUrl || ((url) => url);

    return (
        <div
            className="modal-overlay"
            onClick={onClose}
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.8)',
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 'var(--space-4)'
            }}
        >
            <div
                className="modal-content"
                onClick={e => e.stopPropagation()}
                style={{
                    background: 'var(--color-bg-primary)',
                    borderRadius: 'var(--radius-xl)',
                    maxWidth: '900px',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    width: '100%'
                }}
            >
                {/* Header */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: 'var(--space-4)',
                    borderBottom: '1px solid var(--color-border-light)'
                }}>
                    <h2 style={{ fontSize: 'var(--font-size-xl)' }}>{creative.name}</h2>
                    <button
                        onClick={onClose}
                        className="btn btn-ghost"
                        style={{ padding: 'var(--space-2)' }}
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Content */}
                <div style={{ padding: 'var(--space-6)' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-6)' }}>
                        {/* Media */}
                        <div>
                            {creative.isVideo && creative.videoUrl ? (
                                <video
                                    src={getUrl(creative.videoUrl)}
                                    controls
                                    autoPlay={false}
                                    poster={getUrl(creative.thumbnailUrl)}
                                    style={{
                                        width: '100%',
                                        borderRadius: 'var(--radius-lg)',
                                        background: '#000'
                                    }}
                                />
                            ) : creative.isVideo ? (
                                <div style={{
                                    width: '100%',
                                    aspectRatio: '1',
                                    background: 'var(--color-bg-tertiary)',
                                    borderRadius: 'var(--radius-lg)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 'var(--space-2)'
                                }}>
                                    <Play size={48} style={{ opacity: 0.5 }} />
                                    <p className="text-secondary">Video preview not available</p>
                                    <p className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>
                                        Video may require Meta Business access
                                    </p>
                                </div>
                            ) : (
                                <img
                                    src={getUrl(creative.imageUrl || creative.thumbnailUrl)}
                                    alt={creative.name}
                                    style={{
                                        width: '100%',
                                        borderRadius: 'var(--radius-lg)'
                                    }}
                                />
                            )}
                        </div>

                        {/* Details */}
                        <div>
                            {/* Status Badge */}
                            <div style={{ marginBottom: 'var(--space-4)' }}>
                                <span style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-1)',
                                    padding: 'var(--space-1) var(--space-3)',
                                    borderRadius: 'var(--radius-full)',
                                    fontSize: 'var(--font-size-sm)',
                                    background: creative.status === 'ACTIVE' ? 'var(--color-success-bg)' :
                                        creative.status === 'PAUSED' ? 'var(--color-warning-bg)' : 'var(--color-bg-tertiary)',
                                    color: creative.status === 'ACTIVE' ? 'var(--color-success)' :
                                        creative.status === 'PAUSED' ? 'var(--color-warning)' : 'var(--color-text-secondary)'
                                }}>
                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'currentColor' }} />
                                    {creative.status}
                                    {creative.isVideo && ' • Video'}
                                </span>
                            </div>

                            {/* Ad Copy */}
                            {creative.adCopy && (
                                <div style={{ marginBottom: 'var(--space-4)' }}>
                                    <h4 style={{ marginBottom: 'var(--space-2)', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>Ad Copy</h4>
                                    {creative.adCopy.headline && (
                                        <h3 style={{ fontSize: 'var(--font-size-lg)', marginBottom: 'var(--space-2)' }}>
                                            {creative.adCopy.headline}
                                        </h3>
                                    )}
                                    {creative.adCopy.body && (
                                        <p style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-2)' }}>
                                            {creative.adCopy.body}
                                        </p>
                                    )}
                                    {creative.adCopy.ctaType && (
                                        <span style={{
                                            display: 'inline-block',
                                            padding: 'var(--space-1) var(--space-2)',
                                            background: 'var(--color-primary-bg)',
                                            color: 'var(--color-primary)',
                                            borderRadius: 'var(--radius-md)',
                                            fontSize: 'var(--font-size-xs)',
                                            fontWeight: 600
                                        }}>
                                            {creative.adCopy.ctaType.replace(/_/g, ' ')}
                                        </span>
                                    )}
                                </div>
                            )}

                            {/* Metadata */}
                            <div style={{
                                padding: 'var(--space-4)',
                                background: 'var(--color-bg-secondary)',
                                borderRadius: 'var(--radius-md)'
                            }}>
                                <h4 style={{ marginBottom: 'var(--space-3)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>Details</h4>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span className="text-secondary">Created</span>
                                        <span>{creative.createdTime ? new Date(creative.createdTime).toLocaleDateString() : 'N/A'}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span className="text-secondary">Type</span>
                                        <span>{creative.isVideo ? 'Video' : 'Image'}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span className="text-secondary">Platform</span>
                                        <span style={{ textTransform: 'capitalize' }}>{creative.platform || 'Meta'}</span>
                                    </div>
                                    {creative.metrics && (
                                        <>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <span className="text-secondary">Impressions</span>
                                                <span>{formatNumber(creative.metrics.impressions || 0)}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <span className="text-secondary">Spend</span>
                                                <span>{formatCurrency(creative.metrics.spend || 0)}</span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Creative Timeline Component
const CreativeTimeline = ({ creatives, dateRange, onCreativeClick, getCacheBustedUrl }) => {
    const [visibleCount, setVisibleCount] = useState(25);

    // Fallback if getCacheBustedUrl not provided
    const getUrl = getCacheBustedUrl || ((url) => url);

    if (!creatives || creatives.length === 0) return null;

    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

    // Generate date labels
    const dateLabels = [];
    const labelCount = Math.min(5, totalDays);
    for (let i = 0; i < labelCount; i++) {
        const date = new Date(startDate);
        date.setDate(date.getDate() + Math.floor((totalDays / (labelCount - 1)) * i));
        dateLabels.push(date);
    }

    const getBarPosition = (creative) => {
        const createdDate = creative.createdTime ? new Date(creative.createdTime) : startDate;
        const start = Math.max(0, (createdDate - startDate) / (endDate - startDate) * 100);

        // If still active, extend to today or end date
        let endPos = 100;
        if (creative.status !== 'ACTIVE' && creative.updatedTime) {
            const updatedDate = new Date(creative.updatedTime);
            endPos = Math.min(100, (updatedDate - startDate) / (endDate - startDate) * 100);
        }

        return { left: start, width: Math.max(5, endPos - start) };
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'ACTIVE': return 'var(--color-success)';
            case 'PAUSED': return 'var(--color-warning)';
            default: return 'var(--color-text-tertiary)';
        }
    };

    return (
        <div className="card">
            {/* Header with count */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-4)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <Clock size={18} />
                    <h3 style={{ fontSize: 'var(--font-size-lg)' }}>Timeline</h3>
                </div>
                <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
                    {creatives.length} creatives
                </span>
            </div>

            {/* Date axis */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-tertiary)',
                marginBottom: 'var(--space-2)',
                paddingLeft: '240px',
                paddingRight: '80px'
            }}>
                {dateLabels.map((date, i) => (
                    <span key={i}>{date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                ))}
            </div>

            {/* Timeline bars - scrollable container */}
            <div style={{
                maxHeight: '600px',
                overflowY: 'auto',
                background: 'var(--color-bg-secondary)',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--space-3)'
            }}>
                {creatives.slice(0, visibleCount).map(creative => {
                    const pos = getBarPosition(creative);
                    return (
                        <div
                            key={creative.id}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                height: '40px',
                                cursor: 'pointer',
                                padding: '0 var(--space-2)',
                                borderRadius: 'var(--radius-sm)',
                                marginBottom: 'var(--space-1)',
                                transition: 'background 0.15s'
                            }}
                            onClick={() => onCreativeClick?.(creative.id)}
                            onMouseOver={(e) => e.currentTarget.style.background = 'var(--color-bg-tertiary)'}
                            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                            title={`${creative.name}\nStatus: ${creative.status}\nCreated: ${creative.createdTime ? new Date(creative.createdTime).toLocaleDateString() : 'N/A'}`}
                        >
                            {/* Mini thumbnail */}
                            <img
                                src={getUrl(creative.thumbnailUrl)}
                                alt=""
                                style={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: 'var(--radius-sm)',
                                    objectFit: 'cover',
                                    flexShrink: 0,
                                    background: 'var(--color-bg-tertiary)'
                                }}
                            />
                            {/* Name - wider */}
                            <span style={{
                                width: '200px',
                                fontSize: 'var(--font-size-sm)',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                flexShrink: 0
                            }}>
                                {creative.name}
                            </span>
                            {/* Timeline bar */}
                            <div style={{
                                flex: 1,
                                height: '20px',
                                background: 'var(--color-bg-tertiary)',
                                borderRadius: 'var(--radius-full)',
                                position: 'relative',
                                overflow: 'hidden'
                            }}>
                                <div
                                    style={{
                                        position: 'absolute',
                                        left: `${pos.left}%`,
                                        width: `${pos.width}%`,
                                        height: '100%',
                                        background: getStatusColor(creative.status),
                                        borderRadius: 'var(--radius-full)',
                                        opacity: creative.status === 'ACTIVE' ? 1 : 0.7
                                    }}
                                />
                            </div>
                            {/* Status */}
                            <span style={{
                                width: '70px',
                                fontSize: 'var(--font-size-xs)',
                                color: getStatusColor(creative.status),
                                textAlign: 'right',
                                flexShrink: 0
                            }}>
                                {creative.status}
                            </span>
                        </div>
                    );
                })}
            </div>

            {/* Load more button */}
            {visibleCount < creatives.length && (
                <button
                    className="btn btn-ghost"
                    onClick={() => setVisibleCount(prev => prev + 25)}
                    style={{ width: '100%', marginTop: 'var(--space-3)' }}
                >
                    Show more ({creatives.length - visibleCount} remaining)
                </button>
            )}

            {/* Legend */}
            <div style={{
                display: 'flex',
                gap: 'var(--space-4)',
                marginTop: 'var(--space-3)',
                fontSize: 'var(--font-size-xs)'
            }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                    <span style={{ width: 12, height: 12, borderRadius: 'var(--radius-full)', background: 'var(--color-success)' }} />
                    Active
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                    <span style={{ width: 12, height: 12, borderRadius: 'var(--radius-full)', background: 'var(--color-warning)', opacity: 0.7 }} />
                    Paused
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                    <span style={{ width: 12, height: 12, borderRadius: 'var(--radius-full)', background: 'var(--color-text-tertiary)', opacity: 0.7 }} />
                    Archived
                </span>
            </div>
        </div>
    );
};

// Daily Spend Chart Component
const DailySpendChart = ({ dailyData, isLoading }) => {
    if (isLoading) {
        return (
            <div style={{ padding: 'var(--space-4)', textAlign: 'center' }}>
                <RefreshCw size={20} className="spinning" style={{ color: 'var(--color-primary)' }} />
                <p className="text-secondary" style={{ fontSize: 'var(--font-size-sm)', marginTop: 'var(--space-2)' }}>Loading daily metrics...</p>
            </div>
        );
    }

    if (!dailyData || dailyData.length === 0) {
        return (
            <div style={{ padding: 'var(--space-3)', textAlign: 'center' }}>
                <p className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>No daily data available</p>
            </div>
        );
    }

    const maxSpend = Math.max(...dailyData.map(d => d.spend || 0), 1);
    const totalSpend = dailyData.reduce((sum, d) => sum + (d.spend || 0), 0);
    const avgDailySpend = totalSpend / dailyData.length;

    return (
        <div style={{ marginBottom: 'var(--space-4)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-3)' }}>
                <h4 style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <BarChart3 size={16} />
                    Daily Spend
                </h4>
                <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
                    Avg: ${avgDailySpend.toFixed(2)}/day
                </span>
            </div>
            <div style={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: '2px',
                height: '80px',
                padding: 'var(--space-2)',
                background: 'var(--color-bg-secondary)',
                borderRadius: 'var(--radius-md)'
            }}>
                {dailyData.map((day, idx) => {
                    const height = maxSpend > 0 ? ((day.spend || 0) / maxSpend) * 100 : 0;
                    const date = new Date(day.date);
                    return (
                        <div
                            key={idx}
                            style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                height: '100%',
                                justifyContent: 'flex-end'
                            }}
                            title={`${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}: $${(day.spend || 0).toFixed(2)}`}
                        >
                            <div
                                style={{
                                    width: '100%',
                                    maxWidth: '20px',
                                    height: `${Math.max(height, 2)}%`,
                                    background: day.spend > avgDailySpend
                                        ? 'var(--color-primary)'
                                        : 'var(--color-primary-light)',
                                    borderRadius: 'var(--radius-sm) var(--radius-sm) 0 0',
                                    transition: 'height 0.3s ease'
                                }}
                            />
                        </div>
                    );
                })}
            </div>
            {/* Date labels */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: 'var(--space-1)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-tertiary)'
            }}>
                {dailyData.length > 0 && (
                    <>
                        <span>{new Date(dailyData[0].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                        {dailyData.length > 1 && (
                            <span>{new Date(dailyData[dailyData.length - 1].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

const AdCreativeAnalyzer = () => {
    const { selectedBrand } = useApp();
    const { currentUser } = useAuth();

    // State
    const [creatives, setCreatives] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetching, setFetching] = useState(false);
    const [platformConnections, setPlatformConnections] = useState([]);
    const [imageCacheBuster, setImageCacheBuster] = useState(Date.now());
    // Changed: Use Set for multiple expansion
    const [expandedCreativeIds, setExpandedCreativeIds] = useState(new Set());
    const [analyzingId, setAnalyzingId] = useState(null);
    const [analysisResults, setAnalysisResults] = useState({});

    // Modal state
    const [modalCreative, setModalCreative] = useState(null);

    // Date range state
    const [selectedDateRange, setSelectedDateRange] = useState(DATE_RANGE_PRESETS.LAST_30_DAYS);
    const [dateRange, setDateRange] = useState(() => getDateRange(DATE_RANGE_PRESETS.LAST_30_DAYS));

    // Filters and sorting
    const [sortBy, setSortBy] = useState('spend');
    const [filterStatus, setFilterStatus] = useState('all');

    // Compare mode
    const [compareMode, setCompareMode] = useState(false);
    const [selectedForCompare, setSelectedForCompare] = useState([]);

    // Tab navigation
    const [activeTab, setActiveTab] = useState('creatives'); // 'creatives' | 'timeline'

    // Daily metrics state
    const [dailyMetrics, setDailyMetrics] = useState({}); // { creativeId: { data: [], loading: false } }

    // Check for Meta connection
    const hasMetaConnection = platformConnections.some(c => c.platform === 'meta' && c.status === 'active');

    // Helper to add cache buster to image URLs
    const getCacheBustedUrl = useCallback((url) => {
        if (!url) return url;
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}_cb=${imageCacheBuster}`;
    }, [imageCacheBuster]);

    // Load cached analysis results on mount
    useEffect(() => {
        if (selectedBrand?.id) {
            const cacheKey = `${ANALYSIS_CACHE_KEY_PREFIX}${selectedBrand.id}`;
            try {
                const cached = localStorage.getItem(cacheKey);
                if (cached) {
                    const { data, timestamp } = JSON.parse(cached);
                    const expiryTime = CACHE_EXPIRY_HOURS * 60 * 60 * 1000;
                    if (Date.now() - timestamp < expiryTime) {
                        setAnalysisResults(data);
                    } else {
                        localStorage.removeItem(cacheKey);
                    }
                }
            } catch (e) {
                console.error('Error loading cached analysis:', e);
            }
        }
    }, [selectedBrand?.id]);

    // Save analysis results to cache when updated
    useEffect(() => {
        if (selectedBrand?.id && Object.keys(analysisResults).length > 0) {
            const cacheKey = `${ANALYSIS_CACHE_KEY_PREFIX}${selectedBrand.id}`;
            try {
                localStorage.setItem(cacheKey, JSON.stringify({
                    data: analysisResults,
                    timestamp: Date.now()
                }));
            } catch (e) {
                console.error('Error caching analysis:', e);
            }
        }
    }, [analysisResults, selectedBrand?.id]);

    // Fetch daily metrics for expanded creatives that have analysis
    useEffect(() => {
        expandedCreativeIds.forEach(id => {
            const creative = creatives.find(c => c.id === id);
            if (creative && analysisResults[id] && !dailyMetrics[id]) {
                // Fetch daily metrics when creative is expanded and has analysis
                const fetchMetrics = async () => {
                    setDailyMetrics(prev => ({
                        ...prev,
                        [id]: { data: null, loading: true }
                    }));

                    try {
                        const params = new URLSearchParams({
                            userId: currentUser.uid,
                            brandId: selectedBrand.id,
                            creativeId: creative.adId,
                            platform: 'meta',
                            startDate: dateRange.startDate,
                            endDate: dateRange.endDate
                        });

                        const response = await fetch(`http://localhost:3001/api/creatives/metrics?${params}`);
                        if (response.ok) {
                            const data = await response.json();
                            setDailyMetrics(prev => ({
                                ...prev,
                                [id]: { data: data.dailyData || [], loading: false }
                            }));
                        } else {
                            setDailyMetrics(prev => ({
                                ...prev,
                                [id]: { data: [], loading: false }
                            }));
                        }
                    } catch (error) {
                        console.error('Error fetching daily metrics:', error);
                        setDailyMetrics(prev => ({
                            ...prev,
                            [id]: { data: [], loading: false }
                        }));
                    }
                };
                fetchMetrics();
            }
        });
    }, [expandedCreativeIds, analysisResults, creatives, dailyMetrics, currentUser?.uid, selectedBrand?.id, dateRange]);

    // Fetch platform connections
    const fetchConnections = useCallback(async () => {
        if (!selectedBrand || !currentUser) return;
        try {
            const response = await fetch(
                `http://localhost:3001/api/campaigns/connections?userId=${currentUser.uid}&brandId=${selectedBrand.id}`
            );
            const data = await response.json();
            setPlatformConnections(data.connections || []);
        } catch (error) {
            console.error('Error fetching connections:', error);
        }
    }, [selectedBrand, currentUser]);

    // Fetch creatives from Meta
    const fetchCreatives = useCallback(async () => {
        if (!selectedBrand || !currentUser || !hasMetaConnection) return;

        setFetching(true);
        // Reset cache buster to force image reload
        setImageCacheBuster(Date.now());
        try {
            const response = await fetch('http://localhost:3001/api/creatives/fetch', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: currentUser.uid,
                    brandId: selectedBrand.id,
                    platform: 'meta',
                    dateRange: dateRange
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to fetch creatives');
            }

            const data = await response.json();

            // Deduplicate creatives by ID to fix duplicate key warning
            const uniqueCreatives = [...new Map(
                (data.creatives || []).map(c => [c.id, c])
            ).values()];

            setCreatives(uniqueCreatives);
        } catch (error) {
            console.error('Error fetching creatives:', error);
        } finally {
            setFetching(false);
            setLoading(false);
        }
    }, [selectedBrand, currentUser, hasMetaConnection, dateRange]);

    // Initial load
    useEffect(() => {
        fetchConnections();
    }, [fetchConnections]);

    // Auto-fetch creatives when connections are loaded
    useEffect(() => {
        if (hasMetaConnection && selectedBrand && currentUser) {
            fetchCreatives();
        } else if (platformConnections.length > 0) {
            setLoading(false);
        }
    }, [hasMetaConnection, selectedBrand, currentUser, fetchCreatives, platformConnections.length]);

    // Handle date range change
    const handleDateRangeChange = (preset) => {
        setSelectedDateRange(preset);
        const newRange = getDateRange(preset);
        setDateRange(newRange);
    };

    // Clear all cached analysis for current brand
    const clearAnalysisCache = () => {
        if (selectedBrand?.id) {
            const cacheKey = `${ANALYSIS_CACHE_KEY_PREFIX}${selectedBrand.id}`;
            localStorage.removeItem(cacheKey);
            setAnalysisResults({});
            setDailyMetrics({});
        }
    };

    // Refetch when date range changes
    useEffect(() => {
        if (hasMetaConnection && selectedBrand && currentUser && !loading) {
            fetchCreatives();
        }
    }, [dateRange]);

    // Toggle expanded row (supports multiple)
    const toggleExpanded = useCallback((id) => {
        setExpandedCreativeIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    }, []);

    // Analyze a creative with AI
    const analyzeCreative = async (creative) => {
        setAnalyzingId(creative.id);
        // Expand the row when analyzing
        setExpandedCreativeIds(prev => new Set(prev).add(creative.id));

        try {
            const response = await fetch('http://localhost:3001/api/creatives/analyze', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: currentUser?.uid,
                    brandId: selectedBrand?.id,
                    imageUrl: creative.imageUrl || creative.thumbnailUrl,
                    creativeId: creative.id,
                    metrics: creative.metrics,
                    adCopy: creative.adCopy,
                    isVideo: creative.isVideo
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Analysis failed');
            }

            const result = await response.json();
            setAnalysisResults(prev => ({ ...prev, [creative.id]: result }));
        } catch (error) {
            console.error('Error analyzing creative:', error);
            setAnalysisResults(prev => ({
                ...prev,
                [creative.id]: {
                    error: true,
                    message: error.message
                }
            }));
        } finally {
            setAnalyzingId(null);
        }
    };

    // Toggle compare selection
    const toggleCompareSelect = (creative) => {
        if (selectedForCompare.find(c => c.id === creative.id)) {
            setSelectedForCompare(prev => prev.filter(c => c.id !== creative.id));
        } else if (selectedForCompare.length < 2) {
            setSelectedForCompare(prev => [...prev, creative]);
        }
    };

    // Fetch daily metrics for a creative
    const fetchDailyMetrics = useCallback(async (creative) => {
        if (!creative?.adId || dailyMetrics[creative.id]?.data) return;

        setDailyMetrics(prev => ({
            ...prev,
            [creative.id]: { data: null, loading: true }
        }));

        try {
            const params = new URLSearchParams({
                userId: currentUser.uid,
                brandId: selectedBrand.id,
                creativeId: creative.adId, // Use adId for insights API
                platform: 'meta',
                startDate: dateRange.startDate,
                endDate: dateRange.endDate
            });

            const response = await fetch(`http://localhost:3001/api/creatives/metrics?${params}`);
            if (response.ok) {
                const data = await response.json();
                setDailyMetrics(prev => ({
                    ...prev,
                    [creative.id]: { data: data.dailyData || [], loading: false }
                }));
            } else {
                setDailyMetrics(prev => ({
                    ...prev,
                    [creative.id]: { data: [], loading: false }
                }));
            }
        } catch (error) {
            console.error('Error fetching daily metrics:', error);
            setDailyMetrics(prev => ({
                ...prev,
                [creative.id]: { data: [], loading: false }
            }));
        }
    }, [currentUser?.uid, selectedBrand?.id, dateRange, dailyMetrics]);

    // Sort and filter creatives
    const sortedCreatives = useMemo(() => {
        return [...creatives]
            .filter(c => filterStatus === 'all' || c.status === filterStatus)
            .sort((a, b) => {
                switch (sortBy) {
                    case 'ctr': return (b.metrics?.ctr || 0) - (a.metrics?.ctr || 0);
                    case 'roas': return (b.metrics?.roas || 0) - (a.metrics?.roas || 0);
                    case 'impressions': return (b.metrics?.impressions || 0) - (a.metrics?.impressions || 0);
                    default: return (b.metrics?.spend || 0) - (a.metrics?.spend || 0);
                }
            });
    }, [creatives, filterStatus, sortBy]);

    // Calculate summary stats
    const summaryStats = useMemo(() => ({
        activeCount: creatives.filter(c => c.status === 'ACTIVE').length,
        totalSpend: creatives.reduce((sum, c) => sum + (c.metrics?.spend || 0), 0),
        avgCtr: creatives.length > 0
            ? creatives.reduce((sum, c) => sum + (c.metrics?.ctr || 0), 0) / creatives.length
            : 0,
        avgRoas: creatives.filter(c => c.metrics?.spend > 0).length > 0
            ? creatives.filter(c => c.metrics?.spend > 0).reduce((sum, c) => sum + (c.metrics?.roas || 0), 0) / creatives.filter(c => c.metrics?.spend > 0).length
            : 0
    }), [creatives]);

    // Get score color
    const getScoreColor = (score) => {
        if (score >= 7) return 'var(--color-success)';
        if (score >= 5) return 'var(--color-warning)';
        return 'var(--color-error)';
    };

    // Get grade from score
    const getGrade = (score) => {
        if (score >= 9) return 'A+';
        if (score >= 8) return 'A';
        if (score >= 7) return 'B+';
        if (score >= 6) return 'B';
        if (score >= 5) return 'C';
        return 'D';
    };

    // Format date for display
    const formatCreativeDate = (creative) => {
        if (!creative.createdTime) return '';
        const created = new Date(creative.createdTime);

        if (creative.status === 'ACTIVE') {
            return `Running since ${created.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
        } else {
            return `Created ${created.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
        }
    };

    // Helper to get valid improvements sorted by priority
    const getValidImprovements = (analysis, creativeId) => {
        if (!analysis?.improvements || !Array.isArray(analysis.improvements)) {
            return [];
        }
        const priorityOrder = { high: 0, medium: 1, low: 2 };
        return analysis.improvements
            .filter(imp => imp && imp.title)
            .sort((a, b) => (priorityOrder[a.priority] ?? 3) - (priorityOrder[b.priority] ?? 3))
            .map((imp, idx) => ({
                ...imp,
                key: `${creativeId}-imp-${idx}-${imp.title?.slice(0, 15).replace(/\s/g, '-')}`
            }));
    };

    // Helper to get valid A/B test ideas
    const getValidAbTestIdeas = (analysis, creativeId) => {
        if (!analysis?.abTestIdeas || !Array.isArray(analysis.abTestIdeas)) {
            return [];
        }
        return analysis.abTestIdeas
            .filter(idea => idea && typeof idea === 'string')
            .map((idea, idx) => ({
                text: idea,
                key: `${creativeId}-ab-${idx}-${idea.slice(0, 15).replace(/\s/g, '-')}`
            }));
    };

    // No brand selected
    if (!selectedBrand) {
        return (
            <div className="animate-fadeIn" style={{ textAlign: 'center', padding: 'var(--space-16)' }}>
                <Search size={64} style={{ margin: '0 auto var(--space-4)', color: 'var(--color-text-tertiary)' }} />
                <h2>Select a Brand</h2>
                <p className="text-secondary">Please select a brand to analyze ad creatives.</p>
            </div>
        );
    }

    // No Meta connection
    if (!loading && !hasMetaConnection) {
        return (
            <div className="animate-fadeIn" style={{ textAlign: 'center', padding: 'var(--space-16)' }}>
                <Target size={64} style={{ margin: '0 auto var(--space-4)', color: 'var(--color-text-tertiary)' }} />
                <h2>Connect Meta Ads</h2>
                <p className="text-secondary" style={{ marginBottom: 'var(--space-4)' }}>
                    Connect your Meta Ads account to analyze your ad creatives.
                </p>
                <a href="/campaigns" className="btn btn-primary">
                    Go to Campaigns to Connect
                </a>
            </div>
        );
    }

    // Compare Mode View
    if (compareMode && selectedForCompare.length === 2) {
        const [creative1, creative2] = selectedForCompare;
        const analysis1 = analysisResults[creative1.id];
        const analysis2 = analysisResults[creative2.id];

        return (
            <div className="animate-fadeIn">
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-6)' }}>
                    <div>
                        <h1 style={{ fontSize: 'var(--font-size-3xl)', marginBottom: 'var(--space-2)' }}>
                            Comparing 2 Creatives
                        </h1>
                    </div>
                    <button
                        className="btn btn-secondary"
                        onClick={() => {
                            setCompareMode(false);
                            setSelectedForCompare([]);
                        }}
                    >
                        <X size={18} />
                        Exit Compare
                    </button>
                </div>

                {/* Side by side comparison */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-6)' }}>
                    {[creative1, creative2].map((creative, idx) => {
                        const other = idx === 0 ? creative2 : creative1;
                        const analysis = analysisResults[creative.id];

                        return (
                            <div key={`compare-${creative.id}`} className="card">
                                {/* Creative Header */}
                                <div style={{ display: 'flex', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
                                    <div
                                        style={{
                                            position: 'relative',
                                            width: '120px',
                                            height: '120px',
                                            borderRadius: 'var(--radius-md)',
                                            overflow: 'hidden',
                                            flexShrink: 0,
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => setModalCreative(creative)}
                                    >
                                        <img
                                            src={getCacheBustedUrl(creative.imageUrl || creative.thumbnailUrl)}
                                            alt={creative.name}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                        {creative.isVideo && (
                                            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)' }}>
                                                <Play size={32} fill="white" color="white" />
                                            </div>
                                        )}
                                        <div style={{
                                            position: 'absolute',
                                            bottom: 4,
                                            right: 4,
                                            background: 'rgba(0,0,0,0.6)',
                                            borderRadius: 'var(--radius-sm)',
                                            padding: '2px 6px'
                                        }}>
                                            <ZoomIn size={14} color="white" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 style={{ fontSize: 'var(--font-size-lg)', marginBottom: 'var(--space-1)' }}>{creative.name}</h3>
                                        <span style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)',
                                            padding: 'var(--space-1) var(--space-2)', borderRadius: 'var(--radius-full)',
                                            fontSize: 'var(--font-size-xs)',
                                            background: creative.status === 'ACTIVE' ? 'var(--color-success-bg)' : 'var(--color-bg-tertiary)',
                                            color: creative.status === 'ACTIVE' ? 'var(--color-success)' : 'var(--color-text-secondary)'
                                        }}>
                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor' }} />
                                            {creative.status}
                                        </span>
                                    </div>
                                </div>

                                {/* Metrics comparison */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                                    {[
                                        { label: 'Impressions', key: 'impressions', format: formatNumber },
                                        { label: 'Clicks', key: 'clicks', format: formatNumber },
                                        { label: 'CTR', key: 'ctr', format: (v) => formatPercent(v), higher: true },
                                        { label: 'Spend', key: 'spend', format: formatCurrency },
                                        { label: 'ROAS', key: 'roas', format: (v) => `${v?.toFixed(2)}x`, higher: true }
                                    ].map(metric => {
                                        const value = creative.metrics?.[metric.key] || 0;
                                        const otherValue = other.metrics?.[metric.key] || 0;
                                        const isWinner = metric.higher ? value > otherValue : value < otherValue;

                                        return (
                                            <div key={metric.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <span className="text-secondary">{metric.label}</span>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontWeight: 600 }}>
                                                    {metric.format(value)}
                                                    {isWinner && value !== otherValue && (
                                                        <CheckCircle size={16} style={{ color: 'var(--color-success)' }} />
                                                    )}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* AI Score */}
                                {analysis && !analysis.error && (
                                    <div style={{ marginTop: 'var(--space-4)', padding: 'var(--space-3)', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span>AI Score</span>
                                            <span style={{ fontWeight: 700, fontSize: 'var(--font-size-xl)', color: getScoreColor(analysis.overallScore) }}>
                                                {getGrade(analysis.overallScore)} ({analysis.overallScore?.toFixed(1)})
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {!analysis && (
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => analyzeCreative(creative)}
                                        disabled={analyzingId === creative.id}
                                        style={{ marginTop: 'var(--space-4)', width: '100%' }}
                                    >
                                        {analyzingId === creative.id ? (
                                            <><RefreshCw size={16} className="spinning" /> Analyzing...</>
                                        ) : (
                                            <><Sparkles size={16} /> Analyze</>
                                        )}
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Comparison Summary */}
                <div className="card" style={{ marginTop: 'var(--space-6)' }}>
                    <h3 style={{ marginBottom: 'var(--space-3)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        <Sparkles size={18} />
                        Comparison Summary
                    </h3>
                    <p className="text-secondary">
                        {(() => {
                            const c1Ctr = creative1.metrics?.ctr || 0;
                            const c2Ctr = creative2.metrics?.ctr || 0;
                            const c1Roas = creative1.metrics?.roas || 0;
                            const c2Roas = creative2.metrics?.roas || 0;

                            let ctrDiff = '';
                            if (c1Ctr > c2Ctr && c2Ctr > 0) {
                                ctrDiff = `${creative1.name} has ${((c1Ctr - c2Ctr) / c2Ctr * 100).toFixed(0)}% higher CTR`;
                            } else if (c2Ctr > c1Ctr && c1Ctr > 0) {
                                ctrDiff = `${creative2.name} has ${((c2Ctr - c1Ctr) / c1Ctr * 100).toFixed(0)}% higher CTR`;
                            } else {
                                ctrDiff = 'Both creatives have similar CTR';
                            }

                            let roasDiff = '';
                            if (c1Roas > c2Roas && c2Roas > 0) {
                                roasDiff = `${creative1.name} delivers ${((c1Roas - c2Roas) / c2Roas * 100).toFixed(0)}% better ROAS`;
                            } else if (c2Roas > c1Roas && c1Roas > 0) {
                                roasDiff = `${creative2.name} delivers ${((c2Roas - c1Roas) / c1Roas * 100).toFixed(0)}% better ROAS`;
                            } else {
                                roasDiff = 'Both creatives have similar ROAS';
                            }

                            return `${ctrDiff}. ${roasDiff}. Consider pausing the underperformer and reallocating budget.`;
                        })()}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="animate-fadeIn">
            {/* Modal */}
            {modalCreative && (
                <CreativeDetailModal creative={modalCreative} onClose={() => setModalCreative(null)} getCacheBustedUrl={getCacheBustedUrl} />
            )}

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-6)' }}>
                <div>
                    <h1 style={{ fontSize: 'var(--font-size-3xl)', marginBottom: 'var(--space-2)' }}>
                        Creative Analyzer
                    </h1>
                    <p className="text-secondary">
                        Analyze and optimize your ad creatives with AI-powered insights
                    </p>
                </div>

                <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
                    {/* Compare Mode Toggle */}
                    <button
                        className={`btn ${compareMode ? 'btn-primary' : 'btn-ghost'}`}
                        onClick={() => {
                            setCompareMode(!compareMode);
                            setSelectedForCompare([]);
                        }}
                    >
                        <GitCompare size={18} />
                        Compare ({selectedForCompare.length}/2)
                    </button>

                    {/* Date Range Selector */}
                    <select
                        value={selectedDateRange}
                        onChange={(e) => handleDateRangeChange(e.target.value)}
                        className="select-field"
                        style={{ minWidth: '150px' }}
                    >
                        {Object.entries(DATE_RANGE_LABELS).map(([key, label]) => (
                            <option key={key} value={key}>{label}</option>
                        ))}
                    </select>

                    {/* Refresh Button */}
                    <button
                        className="btn btn-secondary"
                        onClick={fetchCreatives}
                        disabled={fetching}
                    >
                        <RefreshCw size={18} className={fetching ? 'spinning' : ''} />
                        {fetching ? 'Syncing...' : 'Sync'}
                    </button>
                </div>
            </div>

            {/* Compare Mode Instruction Banner */}
            {compareMode && selectedForCompare.length < 2 && (
                <div style={{
                    background: 'var(--color-primary-bg)',
                    border: '1px solid var(--color-primary)',
                    borderRadius: 'var(--radius-lg)',
                    padding: 'var(--space-4)',
                    marginBottom: 'var(--space-4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                        <GitCompare size={20} style={{ color: 'var(--color-primary)' }} />
                        <span>
                            Select <strong>{2 - selectedForCompare.length}</strong> creative{selectedForCompare.length === 1 ? '' : 's'} to compare.
                            Click the checkboxes on the left of each creative.
                        </span>
                    </div>
                    <button
                        className="btn btn-ghost"
                        onClick={() => {
                            setCompareMode(false);
                            setSelectedForCompare([]);
                        }}
                    >
                        Cancel
                    </button>
                </div>
            )}

            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-4" style={{ marginBottom: 'var(--space-6)' }}>
                <div className="stat-card">
                    <div className="stat-label">Active Creatives</div>
                    <div className="stat-value">{summaryStats.activeCount}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">Total Spend</div>
                    <div className="stat-value">{formatCurrency(summaryStats.totalSpend)}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">Avg CTR</div>
                    <div className="stat-value">{formatPercent(summaryStats.avgCtr)}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">Avg ROAS</div>
                    <div className="stat-value">{summaryStats.avgRoas.toFixed(2)}x</div>
                </div>
            </div>

            {/* Tab Navigation */}
            {!loading && sortedCreatives.length > 0 && (
                <div style={{
                    display: 'flex',
                    gap: 'var(--space-1)',
                    marginBottom: 'var(--space-4)',
                    background: 'var(--color-bg-secondary)',
                    padding: 'var(--space-1)',
                    borderRadius: 'var(--radius-lg)',
                    width: 'fit-content'
                }}>
                    <button
                        className={`btn ${activeTab === 'creatives' ? 'btn-primary' : 'btn-ghost'}`}
                        onClick={() => setActiveTab('creatives')}
                        style={{ borderRadius: 'var(--radius-md)' }}
                    >
                        <ImageIcon size={16} />
                        Creatives ({sortedCreatives.length})
                    </button>
                    <button
                        className={`btn ${activeTab === 'timeline' ? 'btn-primary' : 'btn-ghost'}`}
                        onClick={() => setActiveTab('timeline')}
                        style={{ borderRadius: 'var(--radius-md)' }}
                    >
                        <Clock size={16} />
                        Timeline
                    </button>
                </div>
            )}

            {/* Timeline View - shown when timeline tab is active */}
            {activeTab === 'timeline' && !loading && sortedCreatives.length > 0 && (
                <CreativeTimeline
                    creatives={sortedCreatives}
                    dateRange={dateRange}
                    onCreativeClick={(id) => {
                        setActiveTab('creatives');
                        setExpandedCreativeIds(prev => new Set(prev).add(id));
                    }}
                    getCacheBustedUrl={getCacheBustedUrl}
                />
            )}

            {/* Filters - shown when creatives tab is active */}
            {activeTab === 'creatives' && (
            <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
                <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        <span className="text-secondary">Sort by:</span>
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="select-field"
                        >
                            <option value="spend">Highest Spend</option>
                            <option value="ctr">Highest CTR</option>
                            <option value="roas">Highest ROAS</option>
                            <option value="impressions">Most Impressions</option>
                        </select>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        <span className="text-secondary">Status:</span>
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="select-field"
                        >
                            <option value="all">All</option>
                            <option value="ACTIVE">Active</option>
                            <option value="PAUSED">Paused</option>
                        </select>
                    </div>
                    <span className="text-secondary" style={{ marginLeft: 'auto' }}>
                        {sortedCreatives.length} creative{sortedCreatives.length !== 1 ? 's' : ''}
                    </span>
                </div>
            </div>
            )}

            {/* Loading State */}
            {loading && (
                <div style={{ textAlign: 'center', padding: 'var(--space-12)' }}>
                    <RefreshCw size={48} className="spinning" style={{ margin: '0 auto var(--space-4)', color: 'var(--color-primary)' }} />
                    <p className="text-secondary">Loading creatives from Meta...</p>
                </div>
            )}

            {/* Empty State */}
            {activeTab === 'creatives' && !loading && sortedCreatives.length === 0 && (
                <div className="card" style={{ textAlign: 'center', padding: 'var(--space-12)' }}>
                    <ImageIcon size={48} style={{ margin: '0 auto var(--space-4)', color: 'var(--color-text-tertiary)' }} />
                    <h3>No Creatives Found</h3>
                    <p className="text-secondary">
                        {filterStatus !== 'all'
                            ? `No ${filterStatus.toLowerCase()} creatives in the selected date range.`
                            : 'No ad creatives found for the selected date range.'}
                    </p>
                </div>
            )}

            {/* Creative List */}
            {activeTab === 'creatives' && !loading && sortedCreatives.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                    {sortedCreatives.map(creative => {
                        const isExpanded = expandedCreativeIds.has(creative.id);
                        const analysis = analysisResults[creative.id];
                        const isAnalyzing = analyzingId === creative.id;
                        const isSelectedForCompare = selectedForCompare.find(c => c.id === creative.id);
                        const hasAnalysis = analysis && !analysis.error;

                        return (
                            <div
                                key={creative.id}
                                className="card"
                                style={{
                                    padding: 0,
                                    overflow: 'hidden',
                                    border: hasAnalysis && !isExpanded ? '1px solid var(--color-primary-bg)' : undefined
                                }}
                            >
                                {/* Creative Row */}
                                <div
                                    style={{
                                        display: 'flex',
                                        gap: 'var(--space-4)',
                                        padding: 'var(--space-4)',
                                        background: isSelectedForCompare ? 'var(--color-primary-bg)' : 'transparent',
                                        borderLeft: isSelectedForCompare ? '3px solid var(--color-primary)' : '3px solid transparent'
                                    }}
                                >
                                    {/* Compare checkbox */}
                                    {compareMode && (
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <input
                                                type="checkbox"
                                                checked={!!isSelectedForCompare}
                                                onChange={() => toggleCompareSelect(creative)}
                                                disabled={!isSelectedForCompare && selectedForCompare.length >= 2}
                                                style={{ width: 20, height: 20, cursor: 'pointer' }}
                                            />
                                        </div>
                                    )}

                                    {/* Thumbnail - Increased to 180x180 */}
                                    <div
                                        style={{
                                            position: 'relative',
                                            width: '180px',
                                            height: '180px',
                                            borderRadius: 'var(--radius-md)',
                                            overflow: 'hidden',
                                            flexShrink: 0,
                                            background: 'var(--color-bg-secondary)',
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => setModalCreative(creative)}
                                    >
                                        <img
                                            src={getCacheBustedUrl(creative.imageUrl || creative.thumbnailUrl)}
                                            alt={creative.name}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            loading="lazy"
                                        />
                                        {creative.isVideo && (
                                            <div style={{
                                                position: 'absolute',
                                                inset: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                background: 'rgba(0,0,0,0.4)'
                                            }}>
                                                <Play size={48} fill="white" color="white" />
                                            </div>
                                        )}
                                        {/* Zoom indicator */}
                                        <div style={{
                                            position: 'absolute',
                                            bottom: 8,
                                            right: 8,
                                            background: 'rgba(0,0,0,0.6)',
                                            borderRadius: 'var(--radius-sm)',
                                            padding: '4px 8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 4
                                        }}>
                                            <ZoomIn size={14} color="white" />
                                            <span style={{ color: 'white', fontSize: 'var(--font-size-xs)' }}>View</span>
                                        </div>
                                    </div>

                                    {/* Info */}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-2)' }}>
                                            <div>
                                                <h3 style={{ fontSize: 'var(--font-size-lg)', marginBottom: 'var(--space-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '400px' }}>
                                                    {creative.name}
                                                </h3>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                                                    <span style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 'var(--space-1)',
                                                        padding: 'var(--space-1) var(--space-2)',
                                                        borderRadius: 'var(--radius-full)',
                                                        fontSize: 'var(--font-size-xs)',
                                                        background: creative.status === 'ACTIVE' ? 'var(--color-success-bg)' : creative.status === 'PAUSED' ? 'var(--color-warning-bg)' : 'var(--color-bg-tertiary)',
                                                        color: creative.status === 'ACTIVE' ? 'var(--color-success)' : creative.status === 'PAUSED' ? 'var(--color-warning)' : 'var(--color-text-secondary)'
                                                    }}>
                                                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor' }} />
                                                        {creative.status}
                                                    </span>
                                                    {creative.isVideo && (
                                                        <span className="text-secondary" style={{ fontSize: 'var(--font-size-xs)', display: 'flex', alignItems: 'center', gap: 4 }}>
                                                            <Play size={12} /> Video
                                                        </span>
                                                    )}
                                                    <span className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>
                                                        {formatCreativeDate(creative)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Metrics Row */}
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(5, 1fr)',
                                            gap: 'var(--space-4)',
                                            marginTop: 'var(--space-3)',
                                            paddingTop: 'var(--space-3)',
                                            borderTop: '1px solid var(--color-border-light)'
                                        }}>
                                            <div>
                                                <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>Impressions</div>
                                                <div style={{ fontWeight: 600 }}>{formatNumber(creative.metrics?.impressions || 0)}</div>
                                            </div>
                                            <div>
                                                <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>Clicks</div>
                                                <div style={{ fontWeight: 600 }}>{formatNumber(creative.metrics?.clicks || 0)}</div>
                                            </div>
                                            <div>
                                                <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>CTR</div>
                                                <div style={{ fontWeight: 600, color: (creative.metrics?.ctr || 0) >= 1.5 ? 'var(--color-success)' : (creative.metrics?.ctr || 0) >= 1 ? 'inherit' : 'var(--color-error)' }}>
                                                    {formatPercent(creative.metrics?.ctr || 0)}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>Spend</div>
                                                <div style={{ fontWeight: 600 }}>{formatCurrency(creative.metrics?.spend || 0)}</div>
                                            </div>
                                            <div>
                                                <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>ROAS</div>
                                                <div style={{ fontWeight: 600, color: (creative.metrics?.roas || 0) >= 2 ? 'var(--color-success)' : (creative.metrics?.roas || 0) >= 1 ? 'inherit' : 'var(--color-error)' }}>
                                                    {(creative.metrics?.roas || 0).toFixed(2)}x
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                        {/* Analysis Indicator Badge */}
                                        {hasAnalysis && !isExpanded && (
                                            <span style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 'var(--space-1)',
                                                padding: 'var(--space-1) var(--space-2)',
                                                background: 'var(--color-primary-bg)',
                                                color: 'var(--color-primary)',
                                                borderRadius: 'var(--radius-full)',
                                                fontSize: 'var(--font-size-xs)',
                                                fontWeight: 500
                                            }}>
                                                <Sparkles size={12} />
                                                {getGrade(analysis.overallScore)}
                                            </span>
                                        )}

                                        <button
                                            className={`btn ${isExpanded ? 'btn-ghost' : hasAnalysis ? 'btn-secondary' : 'btn-primary'}`}
                                            onClick={() => {
                                                if (isExpanded) {
                                                    toggleExpanded(creative.id);
                                                } else {
                                                    if (!analysis) {
                                                        analyzeCreative(creative);
                                                    } else {
                                                        toggleExpanded(creative.id);
                                                    }
                                                }
                                            }}
                                            disabled={isAnalyzing}
                                        >
                                            {isAnalyzing ? (
                                                <><RefreshCw size={16} className="spinning" /> Analyzing...</>
                                            ) : isExpanded ? (
                                                <><ChevronUp size={16} /> Collapse</>
                                            ) : hasAnalysis ? (
                                                <><Eye size={16} /> View Analysis</>
                                            ) : (
                                                <><Sparkles size={16} /> Analyze</>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {/* Expanded Analysis Panel */}
                                {isExpanded && (
                                    <div style={{
                                        borderTop: '1px solid var(--color-border)',
                                        padding: 'var(--space-6)',
                                        background: 'var(--color-bg-secondary)'
                                    }}>
                                        {isAnalyzing ? (
                                            <div style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
                                                <RefreshCw size={32} className="spinning" style={{ margin: '0 auto var(--space-3)', color: 'var(--color-primary)' }} />
                                                <p>Analyzing creative with AI...</p>
                                            </div>
                                        ) : analysis?.error ? (
                                            <div style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--color-error)' }}>
                                                <AlertTriangle size={32} style={{ margin: '0 auto var(--space-3)' }} />
                                                <p>Analysis failed: {analysis.message}</p>
                                                <button className="btn btn-primary" onClick={() => analyzeCreative(creative)} style={{ marginTop: 'var(--space-3)' }}>
                                                    Try Again
                                                </button>
                                            </div>
                                        ) : analysis ? (
                                            <div style={{ display: 'grid', gridTemplateColumns: '350px 1fr', gap: 'var(--space-6)' }}>
                                                {/* Left: Image + Context */}
                                                <div>
                                                    {/* Full-size image/video */}
                                                    {creative.isVideo && creative.videoUrl ? (
                                                        <video
                                                            src={getCacheBustedUrl(creative.videoUrl)}
                                                            controls
                                                            poster={getCacheBustedUrl(creative.thumbnailUrl)}
                                                            style={{
                                                                width: '100%',
                                                                borderRadius: 'var(--radius-lg)',
                                                                marginBottom: 'var(--space-4)',
                                                                background: '#000'
                                                            }}
                                                        />
                                                    ) : (
                                                        <img
                                                            src={getCacheBustedUrl(creative.imageUrl || creative.thumbnailUrl)}
                                                            alt={creative.name}
                                                            style={{
                                                                width: '100%',
                                                                borderRadius: 'var(--radius-lg)',
                                                                marginBottom: 'var(--space-4)',
                                                                cursor: 'pointer'
                                                            }}
                                                            onClick={() => setModalCreative(creative)}
                                                        />
                                                    )}
                                                    <div style={{ background: 'var(--color-surface)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)' }}>
                                                        <h4 style={{ marginBottom: 'var(--space-2)', fontSize: 'var(--font-size-sm)' }}>Performance Context</h4>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', fontSize: 'var(--font-size-sm)' }}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                <span className="text-secondary">CTR</span>
                                                                <span style={{ color: (creative.metrics?.ctr || 0) >= 1.5 ? 'var(--color-success)' : 'inherit' }}>
                                                                    {formatPercent(creative.metrics?.ctr || 0)}
                                                                </span>
                                                            </div>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                <span className="text-secondary">ROAS</span>
                                                                <span style={{ color: (creative.metrics?.roas || 0) >= 2 ? 'var(--color-success)' : 'inherit' }}>
                                                                    {(creative.metrics?.roas || 0).toFixed(2)}x
                                                                </span>
                                                            </div>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                <span className="text-secondary">Frequency</span>
                                                                <span style={{ color: (creative.metrics?.frequency || 0) > 3 ? 'var(--color-warning)' : 'inherit' }}>
                                                                    {(creative.metrics?.frequency || 0).toFixed(1)}
                                                                </span>
                                                            </div>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                <span className="text-secondary">Reach</span>
                                                                <span>{formatNumber(creative.metrics?.reach || 0)}</span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Daily Spend Chart */}
                                                    <div style={{ marginTop: 'var(--space-4)' }}>
                                                        <DailySpendChart
                                                            dailyData={dailyMetrics[creative.id]?.data}
                                                            isLoading={dailyMetrics[creative.id]?.loading}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Right: Analysis */}
                                                <div>
                                                    {/* Score Header */}
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 'var(--space-4)',
                                                        marginBottom: 'var(--space-4)',
                                                        padding: 'var(--space-4)',
                                                        background: 'var(--color-surface)',
                                                        borderRadius: 'var(--radius-lg)'
                                                    }}>
                                                        <div style={{
                                                            width: '60px',
                                                            height: '60px',
                                                            borderRadius: '50%',
                                                            background: getScoreColor(analysis.overallScore),
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            color: 'white',
                                                            fontWeight: 700,
                                                            fontSize: 'var(--font-size-xl)'
                                                        }}>
                                                            {getGrade(analysis.overallScore)}
                                                        </div>
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700 }}>
                                                                {analysis.overallScore?.toFixed(1)}/10
                                                            </div>
                                                            <div className="text-secondary">{analysis.summary}</div>
                                                        </div>
                                                        <button
                                                            className="btn btn-ghost"
                                                            onClick={() => analyzeCreative(creative)}
                                                            title="Re-analyze with fresh AI insights"
                                                            style={{ flexShrink: 0 }}
                                                        >
                                                            <RefreshCw size={16} />
                                                            Re-analyze
                                                        </button>
                                                    </div>

                                                    {/* Score Breakdown */}
                                                    {analysis.scores && Object.keys(analysis.scores).length > 0 && (
                                                        <div style={{ marginBottom: 'var(--space-4)' }}>
                                                            <h4 style={{ marginBottom: 'var(--space-3)' }}>Score Breakdown</h4>
                                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)' }}>
                                                                {Object.entries(analysis.scores).map(([key, value]) => (
                                                                    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                                                        <div style={{
                                                                            flex: 1,
                                                                            height: '8px',
                                                                            background: 'var(--color-bg-tertiary)',
                                                                            borderRadius: 'var(--radius-full)',
                                                                            overflow: 'hidden'
                                                                        }}>
                                                                            <div style={{
                                                                                height: '100%',
                                                                                width: `${value * 10}%`,
                                                                                background: getScoreColor(value),
                                                                                borderRadius: 'var(--radius-full)'
                                                                            }} />
                                                                        </div>
                                                                        <span style={{ fontSize: 'var(--font-size-xs)', minWidth: '80px' }}>
                                                                            {key.replace(/([A-Z])/g, ' $1').trim()}
                                                                        </span>
                                                                        <span style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)', color: getScoreColor(value) }}>
                                                                            {value?.toFixed(1)}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Improvements */}
                                                    {(() => {
                                                        const improvements = getValidImprovements(analysis, creative.id);
                                                        if (improvements.length === 0) {
                                                            return (
                                                                <div style={{ marginBottom: 'var(--space-4)', padding: 'var(--space-4)', background: 'var(--color-surface)', borderRadius: 'var(--radius-md)' }}>
                                                                    <h4 style={{ marginBottom: 'var(--space-2)' }}>What to Improve</h4>
                                                                    <p className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
                                                                        No specific improvements identified. This creative is performing well!
                                                                    </p>
                                                                </div>
                                                            );
                                                        }
                                                        return (
                                                            <div style={{ marginBottom: 'var(--space-4)' }}>
                                                                <h4 style={{ marginBottom: 'var(--space-3)' }}>What to Improve</h4>
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                                                                    {improvements.map(imp => (
                                                                        <div
                                                                            key={imp.key}
                                                                            style={{
                                                                                padding: 'var(--space-3)',
                                                                                background: 'var(--color-surface)',
                                                                                borderRadius: 'var(--radius-md)',
                                                                                borderLeft: `4px solid ${imp.priority === 'high' ? 'var(--color-error)' : imp.priority === 'medium' ? 'var(--color-warning)' : 'var(--color-success)'}`
                                                                            }}
                                                                        >
                                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-2)' }}>
                                                                                <span style={{ fontWeight: 600 }}>{imp.title}</span>
                                                                                <span style={{
                                                                                    padding: 'var(--space-1) var(--space-2)',
                                                                                    borderRadius: 'var(--radius-full)',
                                                                                    fontSize: 'var(--font-size-xs)',
                                                                                    textTransform: 'uppercase',
                                                                                    background: imp.priority === 'high' ? 'var(--color-error-bg)' : imp.priority === 'medium' ? 'var(--color-warning-bg)' : 'var(--color-success-bg)',
                                                                                    color: imp.priority === 'high' ? 'var(--color-error)' : imp.priority === 'medium' ? 'var(--color-warning)' : 'var(--color-success)'
                                                                                }}>
                                                                                    {imp.priority}
                                                                                </span>
                                                                            </div>
                                                                            {imp.problem && (
                                                                                <p className="text-secondary" style={{ fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-2)' }}>
                                                                                    {imp.problem}
                                                                                </p>
                                                                            )}
                                                                            {imp.solution && (
                                                                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                                                                    <ArrowRight size={14} style={{ marginTop: 2, flexShrink: 0 }} />
                                                                                    <span style={{ fontSize: 'var(--font-size-sm)' }}>{imp.solution}</span>
                                                                                </div>
                                                                            )}
                                                                            {imp.expectedImpact && (
                                                                                <div style={{ color: 'var(--color-success)', fontSize: 'var(--font-size-sm)', display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                                                                                    <TrendingUp size={14} />
                                                                                    Expected: {imp.expectedImpact}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        );
                                                    })()}

                                                    {/* A/B Test Ideas */}
                                                    {(() => {
                                                        const abTestIdeas = getValidAbTestIdeas(analysis, creative.id);
                                                        if (abTestIdeas.length === 0) {
                                                            return (
                                                                <div style={{ padding: 'var(--space-4)', background: 'var(--color-surface)', borderRadius: 'var(--radius-md)' }}>
                                                                    <h4 style={{ marginBottom: 'var(--space-2)' }}>A/B Test Ideas</h4>
                                                                    <p className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
                                                                        No A/B test suggestions available at this time.
                                                                    </p>
                                                                </div>
                                                            );
                                                        }
                                                        return (
                                                            <div>
                                                                <h4 style={{ marginBottom: 'var(--space-3)' }}>A/B Test Ideas</h4>
                                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                                                                    {abTestIdeas.map(idea => (
                                                                        <div
                                                                            key={idea.key}
                                                                            style={{
                                                                                padding: 'var(--space-2) var(--space-3)',
                                                                                background: 'var(--color-surface)',
                                                                                borderRadius: 'var(--radius-full)',
                                                                                fontSize: 'var(--font-size-sm)'
                                                                            }}
                                                                        >
                                                                            {idea.text}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        );
                                                    })()}

                                                    {/* Conversational Chat Interface */}
                                                    <AnalysisChat
                                                        creative={creative}
                                                        analysis={analysis}
                                                        metrics={creative.metrics}
                                                        adCopy={creative.adCopy}
                                                        userId={currentUser?.uid}
                                                        brandId={selectedBrand?.id}
                                                    />
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <style>{`
                .spinning {
                    animation: spin 1s linear infinite;
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default AdCreativeAnalyzer;
